
import html2canvas from 'html2canvas';

function sanitizeMinimalGradientsInClone(clonedDoc: Document) {
    try {
        console.log('Checking for problematic gradients in cloned document...');
        let sanitizedCount = 0;
        
        // Get all elements in the cloned document
        const allElements = clonedDoc.querySelectorAll('*');
        
        allElements.forEach((el) => {
            const element = el as HTMLElement;
            try {
                const computedStyle = clonedDoc.defaultView?.getComputedStyle(element);
                
                if (computedStyle?.backgroundImage && computedStyle.backgroundImage !== 'none') {
                    const bgImage = computedStyle.backgroundImage;
                    
                    // Only remove gradients with calc() expressions that commonly cause addColorStop errors
                    if (bgImage.includes('gradient') && bgImage.includes('calc(')) {
                        console.warn('Removing calc() gradient:', bgImage.substring(0, 100) + '...');
                        element.style.backgroundImage = 'none';
                        sanitizedCount++;
                    }
                }
            } catch (styleError) {
                // If we can't read the style safely, it's better to leave it alone
                console.warn('Could not read styles for element:', element.tagName);
            }
        });
        
        console.log(`Sanitized ${sanitizedCount} calc() gradients`);
        
    } catch (error) {
        console.warn('Error sanitizing gradients in clone:', error);
    }
}

function sanitizeAllGradientsInClone(clonedDoc: Document) {
    try {
        console.log('Aggressive gradient removal for fallback...');
        let sanitizedCount = 0;
        
        const allElements = clonedDoc.querySelectorAll('*');
        
        allElements.forEach((el) => {
            const element = el as HTMLElement;
            try {
                const computedStyle = clonedDoc.defaultView?.getComputedStyle(element);
                
                if (computedStyle?.backgroundImage && computedStyle.backgroundImage !== 'none') {
                    const bgImage = computedStyle.backgroundImage;
                    
                    // Remove ALL gradients as fallback to prevent crashes
                    if (bgImage.includes('gradient')) {
                        element.style.backgroundImage = 'none';
                        sanitizedCount++;
                    }
                }
            } catch (styleError) {
                // Continue processing other elements
            }
        });
        
        console.log(`Fallback: removed ${sanitizedCount} gradients`);
        
    } catch (error) {
        console.warn('Error in fallback gradient sanitization:', error);
    }
}

function createScreenshotPromiseWithTimeout(element: HTMLElement, options: any, timeoutMs: number = 15000) {
    return Promise.race([
        html2canvas(element, options),
        new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Screenshot timeout')), timeoutMs)
        )
    ]);
}

async function attemptScreenshotCapture(useMinimalSanitization: boolean = true) {
    // Get viewport dimensions with reasonable limits
    const maxWidth = 4000;  // Limit to prevent excessive memory usage
    const maxHeight = 4000;
    
    const width = Math.min(Math.max(document.documentElement.scrollWidth, window.innerWidth, 10), maxWidth);
    const height = Math.min(Math.max(document.documentElement.scrollHeight, window.innerHeight, 10), maxHeight);
    
    // Use device pixel ratio for high-quality screenshots, but cap it to prevent excessive memory usage
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    
    console.log('Taking screenshot with dimensions:', width, 'x', height, 'scale:', scale);
    
    return await createScreenshotPromiseWithTimeout(document.body, {
        useCORS: true,
        allowTaint: false,
        scale: scale,  // High-quality scaling for exact screenshots
        width: width,
        height: height,
        scrollX: 0,
        scrollY: 0,
        windowWidth: width,
        windowHeight: height,
        backgroundColor: null,  // Preserve original background
        logging: false,
        removeContainer: true,
        imageTimeout: 5000,  // Wait for images to load
        // Skip elements that commonly cause issues
        ignoreElements: (element: HTMLElement) => {
            return element.tagName === 'SCRIPT' ||
                   element.tagName === 'NOSCRIPT' ||
                   element.classList.contains('webpack-dev-server-message');
        },
        onclone: (clonedDoc: Document) => {
            if (useMinimalSanitization) {
                // Try with minimal sanitization first for best visual quality
                sanitizeMinimalGradientsInClone(clonedDoc);
            } else {
                // Fallback: more aggressive sanitization if first attempt failed
                sanitizeAllGradientsInClone(clonedDoc);
            }
        }
    });
}

export async function captureScreenshot() {
    try {
        // First attempt: minimal sanitization for best visual quality
        const canvas = await attemptScreenshotCapture(true);
        console.log('High-quality screenshot captured successfully');
        const base64image = canvas.toDataURL('image/png', 0.95);  // High quality
        return base64image;
        
    } catch (error) {
        console.warn('High-quality screenshot failed, trying fallback:', error);
        
        // Check if this is the specific gradient error we're trying to avoid
        const isGradientError = error.message && (
            error.message.includes('addColorStop') ||
            error.message.includes('CanvasGradient') ||
            error.message.includes('non-finite')
        );
        
        if (isGradientError) {
            try {
                // Second attempt: more aggressive gradient removal
                console.log('Retrying with aggressive gradient sanitization...');
                const canvas = await attemptScreenshotCapture(false);
                console.log('Fallback screenshot captured successfully');
                const base64image = canvas.toDataURL('image/png', 0.95);
                return base64image;
            } catch (fallbackError) {
                console.error('Fallback screenshot also failed:', fallbackError);
                throw fallbackError;
            }
        } else {
            // Different error, just re-throw
            throw error;
        }
    }
}
